import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { DOCUMENT } from '../constants/documentTheme';

const SafeImage = ({ src, fallbackSrc, alt, sx, ...props }) => {
  const initialSrc = (src && String(src).trim()) ? src : fallbackSrc;
  const [imgSrc, setImgSrc] = useState(initialSrc);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setImgSrc((src && String(src).trim()) ? src : fallbackSrc);
    setHasError(false);
  }, [src, fallbackSrc]);

  const handleError = () => {
    if (!hasError && fallbackSrc && imgSrc !== fallbackSrc) {
      setImgSrc(fallbackSrc);
      setHasError(true);
    }
  };

  return (
    <CardMedia
      component="img"
      src={imgSrc || fallbackSrc || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=600&h=300&fit=crop&q=80'}
      alt={alt}
      onError={handleError}
      sx={sx}
      {...props}
    />
  );
};

export const partnerCardTitleSx = {
  fontFamily: DOCUMENT.display,
  fontWeight: 400,
  fontSize: '1.35rem',
  mb: 1,
  lineHeight: 1.25,
  color: DOCUMENT.ink,
  minHeight: '2.7rem',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

export const partnerCardTitleWideSx = {
  ...partnerCardTitleSx,
  fontSize: '1.2rem',
  minHeight: 0,
  mb: 1,
};

export const PartnerCardDatePanel = ({
  startLabel,
  endLabel,
  startValue,
  endValue,
  caption,
  compact = false,
  inline = false,
}) => {
  if (inline) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.35, minHeight: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, color: DOCUMENT.ink3, flexShrink: 0 }}>
          {startLabel}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, color: DOCUMENT.ink, fontSize: 12.5 }} noWrap>
          {startValue}
        </Typography>
        <Box sx={{ width: 14, height: 1.5, bgcolor: DOCUMENT.oxblood, opacity: 0.45, borderRadius: 1, flexShrink: 0 }} />
        <Typography variant="body2" sx={{ fontWeight: 700, color: DOCUMENT.ink, fontSize: 12.5 }} noWrap>
          {endValue}
        </Typography>
        {caption ? (
          <Typography variant="caption" sx={{ color: DOCUMENT.ink3, fontSize: 11 }} noWrap>
            {caption}
          </Typography>
        ) : null}
      </Box>
    );
  }

  return (
    <Paper
      sx={{
        p: compact ? 1.25 : 2,
        bgcolor: 'rgba(36,19,24,0.03)',
        border: `1px solid ${DOCUMENT.rule}`,
        borderRadius: 3,
        mb: compact ? 1 : 2,
      }}
    >
    <Stack spacing={compact ? 0.75 : 1.5}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5, color: DOCUMENT.ink3 }}>
          {startLabel}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5, color: DOCUMENT.ink3 }}>
          {endLabel}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="body1" sx={{ fontWeight: 700, fontSize: compact ? '0.95rem' : '1.1rem' }}>
          {startValue}
        </Typography>
        <Box
          sx={{
            width: compact ? 24 : 40,
            height: 2,
            bgcolor: DOCUMENT.oxblood,
            opacity: 0.45,
            borderRadius: 1,
          }}
        />
        <Typography variant="body1" sx={{ fontWeight: 700, fontSize: compact ? '0.95rem' : '1.1rem' }}>
          {endValue}
        </Typography>
      </Box>
        {caption ? (
          <Typography variant="caption" sx={{ textAlign: 'center', color: DOCUMENT.ink3 }}>
            {caption}
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
};

const chipSx = (tone = 'status') => ({
  fontWeight: 600,
  fontSize: 11,
  height: 22,
  ...(tone === 'accent'
    ? {
      backgroundColor: 'rgba(255,255,255,0.95)',
      color: DOCUMENT.oxblood,
      border: `1px solid ${DOCUMENT.oxblood}`,
    }
    : tone === 'highlight'
      ? {
        backgroundColor: 'rgba(201,169,97,0.95)',
        color: DOCUMENT.nav,
        border: '1px solid #C9A961',
      }
      : tone === 'catalog'
        ? {
          backgroundColor: 'rgba(246,239,228,0.95)',
          color: DOCUMENT.oxblood,
          border: '1px solid rgba(201,169,97,0.55)',
        }
        : {
          backgroundColor: 'rgba(255,255,255,0.9)',
          color: tone === 'type' ? 'rgba(0,0,0,0.7)' : DOCUMENT.ink,
          borderColor: 'rgba(255,255,255,0.3)',
        }),
});

const cardMenuPaperSx = {
  minWidth: 188,
  mt: 0.5,
  borderRadius: 0,
  border: `1px solid ${DOCUMENT.rule}`,
  backgroundColor: DOCUMENT.surface,
  boxShadow: 'none',
  '& .MuiMenuItem-root': {
    fontFamily: DOCUMENT.sans,
    fontSize: 14,
    color: DOCUMENT.ink,
    '&:hover': { backgroundColor: DOCUMENT.ivory },
    '&.Mui-disabled': { opacity: 0.5 },
  },
};

const CardOverflowMenu = ({ menuItems, onMenuClick, buttonSx }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const openMenu = (event) => {
    event.stopPropagation();
    if (menuItems.length) {
      setAnchorEl(event.currentTarget);
      return;
    }
    onMenuClick?.(event);
  };

  return (
    <Box data-partner-card-menu onClick={(event) => event.stopPropagation()}>
      <IconButton
        size="small"
        aria-label="more"
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        onClick={openMenu}
        sx={buttonSx}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableScrollLock
        PaperProps={{ elevation: 0, sx: cardMenuPaperSx }}
      >
        {menuItems.map((item) => (
          <MenuItem
            key={item.label}
            disabled={item.disabled}
            onClick={(event) => {
              event.stopPropagation();
              setAnchorEl(null);
              item.onClick?.(event);
            }}
            sx={item.danger ? { color: '#8C2B22' } : undefined}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

const PartnerWorkspaceCard = ({
  testId,
  coverSrc,
  fallbackSrc,
  alt,
  chips = [],
  icon,
  coverExtra,
  beforeContent,
  menuItems = [],
  onMenuClick,
  onClick,
  actionLabel,
  actionIcon,
  onAction,
  secondaryActionLabel,
  secondaryActionIcon,
  onSecondaryAction,
  layout = 'portrait',
  children,
}) => {
  const wide = layout === 'wide';
  const overflowMenu = (menuItems.length > 0 || onMenuClick) ? (
    <CardOverflowMenu
      menuItems={menuItems}
      onMenuClick={onMenuClick}
      buttonSx={{ color: DOCUMENT.ink2, width: 28, height: 28 }}
    />
  ) : null;

  if (layout === 'stage') {
    return (
      <Card
        data-testid={testId}
        data-layout={layout}
        onClick={onClick}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
          position: 'relative',
          bgcolor: DOCUMENT.surface,
          border: `1px solid ${DOCUMENT.ruleHard}`,
          borderRadius: 0,
          overflow: 'hidden',
          boxShadow: 'none',
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': { borderColor: DOCUMENT.accent },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'stretch', flex: 1, minHeight: 156 }}>
          <Box sx={{ width: { xs: 132, sm: 188 }, flexShrink: 0, position: 'relative', bgcolor: DOCUMENT.oxblood }}>
            <SafeImage
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              src={coverSrc}
              fallbackSrc={fallbackSrc}
              alt={alt}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', p: 1.75, pr: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: '100%' }}>{children}</Box>
              {overflowMenu}
            </Box>
          </Box>
        </Box>
        {beforeContent}
      </Card>
    );
  }

  if (layout === 'compact') {
    return (
      <Card
        data-testid={testId}
        data-layout={layout}
        onClick={onClick}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          position: 'relative',
          bgcolor: DOCUMENT.surface,
          border: `1px solid ${DOCUMENT.ruleHard}`,
          borderRadius: 0,
          overflow: 'hidden',
          boxShadow: 'none',
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': { borderColor: DOCUMENT.accent },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, px: 1.25, py: 1 }}>
          <Box sx={{ width: 56, height: 56, flexShrink: 0, bgcolor: DOCUMENT.oxblood, overflow: 'hidden' }}>
            <SafeImage
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              src={coverSrc}
              fallbackSrc={fallbackSrc}
              alt={alt}
            />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {chips.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.25 }}>
                {chips.map((chip) => (
                  <Chip
                    key={chip.label}
                    icon={chip.icon}
                    label={chip.label}
                    size="small"
                    variant={chip.tone === 'type' ? 'outlined' : 'filled'}
                    sx={{ ...chipSx(chip.tone), height: 20, fontSize: 10 }}
                  />
                ))}
              </Box>
            )}
            {children}
            {(actionLabel || secondaryActionLabel) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mt: 0.5 }}>
                {actionLabel && (
                  <Button
                    onClick={(event) => {
                      event.stopPropagation();
                      onAction?.(event);
                    }}
                    sx={{
                      p: 0,
                      minWidth: 0,
                      minHeight: 0,
                      color: DOCUMENT.oxblood,
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      '&:hover': { background: 'none', textDecoration: 'underline' },
                    }}
                  >
                    {actionLabel}
                  </Button>
                )}
                {secondaryActionLabel && (
                  <Button
                    onClick={(event) => {
                      event.stopPropagation();
                      onSecondaryAction?.(event);
                    }}
                    sx={{
                      p: 0,
                      minWidth: 0,
                      minHeight: 0,
                      color: DOCUMENT.ink2,
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      '&:hover': { background: 'none', textDecoration: 'underline', color: DOCUMENT.oxblood },
                    }}
                  >
                    {secondaryActionLabel}
                  </Button>
                )}
              </Box>
            )}
          </Box>
          {overflowMenu}
        </Box>
        {beforeContent}
      </Card>
    );
  }

  return (
    <Card
      data-testid={testId}
      data-layout={layout}
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        position: 'relative',
        bgcolor: DOCUMENT.surface,
        border: `1px solid ${DOCUMENT.ruleHard}`,
        borderRadius: 4,
        overflow: 'hidden',
        boxShadow: '0 10px 28px rgba(36,19,24,0.08)',
        transition: 'all 0.3s ease',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: '0 12px 35px rgba(36,19,24,0.14)',
          borderColor: DOCUMENT.accent,
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          flexShrink: 0,
          width: '100%',
        }}
      >
        <SafeImage
          sx={{
            height: wide ? 148 : 200,
            objectFit: 'cover',
          }}
          src={coverSrc}
          fallbackSrc={fallbackSrc}
          alt={alt}
        />
        {coverExtra}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            p: wide ? 1.25 : 2,
            zIndex: 2,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {chips.map((chip) => (
              <Chip
                key={chip.label}
                icon={chip.icon}
                label={chip.label}
                size="small"
                variant={chip.tone === 'type' ? 'outlined' : 'filled'}
                sx={chipSx(chip.tone)}
              />
            ))}
          </Box>
          {(menuItems.length > 0 || onMenuClick) && (
            <CardOverflowMenu
              menuItems={menuItems}
              onMenuClick={onMenuClick}
              buttonSx={{
                backgroundColor: 'rgba(255,255,255,0.9)',
                color: 'rgba(0,0,0,0.7)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,1)',
                  color: 'rgba(0,0,0,0.9)',
                },
              }}
            />
          )}
        </Box>
        {icon && (
          <Avatar
            sx={{
              position: 'absolute',
              bottom: -18,
              left: 16,
              width: 36,
              height: 36,
              bgcolor: 'white',
              color: DOCUMENT.oxblood,
              border: `3px solid ${DOCUMENT.oxblood}`,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            {icon}
          </Avatar>
        )}
      </Box>

      {beforeContent}

      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          pt: wide ? 3 : 3,
          px: 2,
          pb: wide ? 2 : 1.75,
          minWidth: 0,
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
        {(actionLabel || secondaryActionLabel) && (
          <Box sx={{ mt: 'auto', display: 'flex', gap: 1 }}>
            {secondaryActionLabel ? (
              <Button
                variant="outlined"
                fullWidth={!actionLabel}
                startIcon={secondaryActionIcon}
                onClick={(event) => {
                  event.stopPropagation();
                  onSecondaryAction?.(event);
                }}
                sx={{
                  flex: actionLabel ? 1 : undefined,
                  color: DOCUMENT.oxblood,
                  borderColor: DOCUMENT.oxblood,
                  fontWeight: 600,
                  py: wide ? 1.15 : 1.25,
                  borderRadius: 999,
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: DOCUMENT.nav,
                    color: DOCUMENT.nav,
                    background: 'rgba(74,14,21,0.06)',
                    boxShadow: 'none',
                  },
                }}
              >
                {secondaryActionLabel}
              </Button>
            ) : null}
            {actionLabel ? (
              <Button
                variant="contained"
                fullWidth={!secondaryActionLabel}
                startIcon={actionIcon}
                onClick={(event) => {
                  event.stopPropagation();
                  onAction?.(event);
                }}
                sx={{
                  flex: secondaryActionLabel ? 1 : undefined,
                  background: DOCUMENT.oxblood,
                  color: DOCUMENT.ivory,
                  fontWeight: 600,
                  py: wide ? 1.15 : 1.25,
                  borderRadius: 999,
                  boxShadow: 'none',
                  '&:hover': {
                    background: DOCUMENT.nav,
                    boxShadow: 'none',
                  },
                }}
              >
                {actionLabel}
              </Button>
            ) : null}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PartnerWorkspaceCard;
